import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface PageHeroProps {
  title: string;
  subtitle: string;
  imageUrl: string;
  eyebrow?: string;
  ctaLabel?: string;
  ctaPath?: string;
}

export default function PageHero({
  title,
  subtitle,
  imageUrl,
  eyebrow,
  ctaLabel,
  ctaPath,
}: PageHeroProps) {
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative w-full h-[62vh] min-h-[480px] max-h-[680px] flex items-end overflow-hidden">
      {/* Background Image — subtle Ken Burns */}
      <div className="absolute inset-0">
        <img
          src={imageUrl}
          alt={title}
          className={`w-full h-full object-cover object-top transition-transform ease-out ${
            loaded ? 'scale-100 duration-[7000ms]' : 'scale-108 duration-0'
          }`}
          style={{ transformOrigin: 'center 30%' }}
        />
        {/* Layered gradient — dark at bottom for text legibility, lighter at top */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/20" />
        {/* Subtle warm tint */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A1208]/30 via-transparent to-transparent" />
      </div>

      {/* Decorative top rule */}
      <div
        className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A961]/50 to-transparent transition-opacity duration-1000 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ transitionDelay: '600ms' }}
      />

      {/* Content — bottom-anchored, left-aligned */}
      <div className="relative z-10 w-full px-8 md:px-16 lg:px-24 pb-14 md:pb-18">
        {/* Eyebrow */}
        {eyebrow && (
          <div
            className={`flex items-center gap-3 mb-5 transition-all duration-700 ease-out ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
            }`}
            style={{ transitionDelay: loaded ? '200ms' : '0ms' }}
          >
            <div className="w-8 h-px bg-[#C9A961]" />
            <span className="text-[#C9A961] text-xs uppercase tracking-[0.25em] font-medium">
              {eyebrow}
            </span>
          </div>
        )}

        {/* Title */}
        <h1
          className={`text-white font-light text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight max-w-2xl transition-all duration-700 ease-out ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: loaded ? '350ms' : '0ms' }}
        >
          {title}
        </h1>

        {/* Subtitle */}
        <p
          className={`mt-4 text-white/65 text-base md:text-lg font-light leading-relaxed max-w-xl transition-all duration-700 ease-out ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: loaded ? '500ms' : '0ms' }}
        >
          {subtitle}
        </p>

        {/* Optional CTA */}
        {ctaLabel && ctaPath && (
          <div
            className={`mt-8 transition-all duration-700 ease-out ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: loaded ? '650ms' : '0ms' }}
          >
            <button
              onClick={() => navigate(ctaPath)}
              className="inline-flex items-center gap-2.5 bg-[#C9A961] hover:bg-[#B8954F] text-[#1A1A1A] font-semibold text-xs uppercase tracking-[0.2em] px-7 py-3.5 rounded-md transition-colors duration-300 cursor-pointer whitespace-nowrap"
            >
              {ctaLabel}
              <i className="ri-arrow-right-line text-sm"></i>
            </button>
          </div>
        )}
      </div>

      {/* Scroll indicator — centered at bottom */}
      <div
        className={`absolute bottom-8 left-0 right-0 flex flex-col items-center gap-2 z-10 transition-all duration-700 ease-out ${
          loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        style={{ transitionDelay: loaded ? '900ms' : '0ms' }}
      >
        <span className="text-white/40 text-[10px] uppercase tracking-[0.3em] font-medium">
          Scroll
        </span>
        {/* Animated chevron stack */}
        <div className="flex flex-col items-center gap-0.5">
          {[0, 1, 2].map((i) => (
            <i
              key={i}
              className="ri-arrow-down-s-line text-white/60 text-base leading-none"
              style={{
                animation: 'scrollChevron 1.8s ease-in-out infinite',
                animationDelay: `${i * 220}ms`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Bottom fade into page content */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white/8 to-transparent pointer-events-none" />

      <style>{`
        @keyframes scrollChevron {
          0%   { opacity: 0.15; transform: translateY(-4px); }
          50%  { opacity: 0.7;  transform: translateY(2px);  }
          100% { opacity: 0.15; transform: translateY(-4px); }
        }
      `}</style>
    </section>
  );
}
